import PageHero from '@/components/PageHero';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, AlertCircle } from 'lucide-react';

const Seminartermine = () => {
  const springSemester = {
    title: "Frühjahr Semester 2025",
    modules: [
      { number: 1, dates: "22.-23.02. 2025 (Sa/So)" },
      { number: 2, dates: "15.-16.03. 2025 (Sa/So)" },
      { number: 3, dates: "12.-13.04. 2025 (Sa/So)" },
      { number: 4, dates: "24.-25.05. 2025 (Sa/So)" },
      { number: 5, dates: "21.-22.06. 2025 (Sa/So)", hasExam: true },
      { number: 6, dates: "26.-27.07. 2026 (Sa/So)", isAdditional: true }
    ]
  };

  const autumnSemester = {
    title: "Herbst Semester 2025/2026",
    modules: [
      { number: 1, dates: "20.-21.9. 2025 (Sa/So)" },
      { number: 2, dates: "18.-19.10. 2025 (Sa/So)" },
      { number: 3, dates: "15.-16.11. 2025 (Sa/So)" },
      { number: 4, dates: "13.-14.12. 2025 (Sa/So)" },
      { number: 5, dates: "17.-18.01. 2026 (Sa/So)", hasExam: true },
      { number: 6, dates: "07.-08.02. 2026 (Sa/So)", isAdditional: true }
    ]
  };

  const SemesterCard = ({ semester }: { semester: typeof springSemester }) => (
    <GlassCard className="p-8 bg-white/90 border-white/30">
      <h3 className="text-2xl font-bold text-primary-deep mb-6 text-center">
        {semester.title}
      </h3>
      
      <div className="space-y-4">
        {semester.modules.map((module) => (
          <div 
            key={module.number}
            className={`flex items-center justify-between p-4 rounded-lg border ${
              module.isAdditional 
                ? 'bg-orange-50 border-orange-200' 
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center space-x-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                module.isAdditional ? 'bg-orange-500' : 'bg-primary-main'
              }`}>
                {module.number}
              </div>
              <div>
                <div className="font-semibold text-gray-800">
                  Modul {module.number}
                  {module.isAdditional && (
                    <span className="text-sm text-orange-600 ml-2">(Zusatzmodul)</span>
                  )}
                </div>
                {module.hasExam && (
                  <div className="text-sm text-green-600 font-medium">
                    Prüfung findet im Modul statt
                  </div>
                )}
                {module.isAdditional && (
                  <div className="text-sm text-orange-600">
                    Nur für Absolventen aller Module
                  </div>
                )}
              </div>
            </div>
            
            <div className="text-right">
              <div className="flex items-center space-x-2 text-gray-600">
                <Calendar className="h-4 w-4" />
                <span className="font-medium">{module.dates}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  );

  return (
    <>
      <PageHero 
        title="Seminartermine" 
        description="Alle Termine für die Prana Kundalini-Therapie® Ausbildung"
        image="https://images.unsplash.com/photo-1501854140801-50d01698950b"
      />
      
      <section className="py-20 bg-primary-lightest">
        <div className="container mx-auto px-4">
          {/* Important Notice */}
          <div className="max-w-4xl mx-auto mb-12">
            <GlassCard className="p-6 bg-white/90 border-white/30">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">
                    Wichtiger Hinweis zum Frühjahr Semester 2025
                  </h3>
                  <p className="text-blue-700">
                    Es gibt noch wenige freie Plätze! Sichern Sie sich jetzt Ihren Platz 
                    für die spirituelle Ausbildung.
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <SemesterCard semester={springSemester} />
            <SemesterCard semester={autumnSemester} />
          </div>

          {/* Additional Information */}
          <div className="mt-16 max-w-4xl mx-auto">
            <GlassCard className="p-8 bg-white/90 border-white/30">
              <h3 className="text-2xl font-bold text-primary-deep mb-6 text-center">
                Weitere Informationen
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-primary-main mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Dauer</h4>
                      <p className="text-gray-600">Jedes Modul dauert 2 Tage (Samstag und Sonntag)</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-primary-main mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Grupengröße</h4>
                      <p className="text-gray-600">Kleine Gruppen für optimale Betreuung</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-orange-500 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-800">Ausfall durch Krankheit</h4>
                      <p className="text-gray-600">
                        Bei ernsthaftem Ausfall (z.B. Krankheit) besteht die Möglichkeit, 
                        das Modul nachzuholen. Nähere Informationen finden Sie in den AGBs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </GlassCard>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <GlassCard className="p-8 max-w-2xl mx-auto bg-white/90 border-white/30">
              <h3 className="text-2xl font-bold text-primary-deep mb-4">
                Bereit für Ihre Anmeldung?
              </h3>
              <p className="text-gray-700 mb-6">
                Sichern Sie sich Ihren Platz in der nächsten Ausbildungsrunde
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary-main hover:bg-primary-deep">
                  <Link to="/anmeldung">
                    <Calendar className="mr-2 h-5 w-5" />
                    Jetzt anmelden
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary-main text-primary-main hover:bg-primary-main hover:text-white">
                  <Link to="/preise">
                    <span className="mr-2">€</span>
                    Preise ansehen
                  </Link>
                </Button>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>
    </>
  );
};

export default Seminartermine;